import { useCallback, useMemo } from 'react';
import { IconButton, Skeleton } from '@mui/material';

import { FCX } from '@models';
import Button from '@components/Button';
import { IconPencil } from '@svg';
import useTableSort from '@core/hooks/useTableSort';
import { TableContext } from '@components/Table/TableContext';
import { iTableConfigItem } from '@components/Table/TableConfig';
import { formatDefaultDate, formatNumber, formatPercentage } from '@core/utils/string';
import useCommonEnums, { SurveyStatusIdEnum } from '@core/hooks/useCommonEnums';
import { ExtendedSurvey, GetSurveyInitialDataResponse, GetExtendedSurveysResponse } from '@core/api/useSurveys';
import DownloadButton from '@pages/Surveys/components/DownloadButton';
import { useDownloadClosedSurveyReports } from '@core/api/useSurveyFile';

const ClosedSurveyActionButton = ({ surveyId }: { surveyId: number }) => {
    const {
        isLoading,
        doFetchAndDownload
    } = useDownloadClosedSurveyReports(surveyId);

    return <DownloadButton isLoading={isLoading} doFetchAndDownload={doFetchAndDownload} />;
};

const SurveysTableConfig: FCX<{
    actions: {
        onEdit: (_survey?: ExtendedSurvey) => void;
        onLaunch: (_survey?: ExtendedSurvey) => void;
        onClose: (_survey?: ExtendedSurvey) => void;
    };
    customFielding: GetSurveyInitialDataResponse['customFielding'];
    data: GetExtendedSurveysResponse;
    isExtendedDataLoading: boolean;
}> = ({
    actions,
    children,
    customFielding,
    data,
    isExtendedDataLoading,
}) => {
    const {
        findCommonNameById,
        isLoading: isLoadingCommon
    } = useCommonEnums();

    const {
        sortedResults,
        sortConfig
    } = useTableSort(data);

    const getCustomFielding = useCallback(
        (id?: number | null): string => (customFielding ?? []).find((cf) => cf.id === id)?.name ?? '',
        [customFielding],
    );

    const getSampleSize = useCallback((survey?: ExtendedSurvey) => {
        let sampleSize = '';

        switch (survey?.statusId) {
            case SurveyStatusIdEnum.Draft:
                sampleSize = formatNumber(survey.requestedSampleSize);
                break;
            case SurveyStatusIdEnum.Closed:
                sampleSize = formatNumber(survey.completedSampleSize);
                break;
            case SurveyStatusIdEnum.Live:
                sampleSize = formatPercentage(survey.completedSampleSizePercentage);
                break;
            default:
                break;
        }

        return sampleSize;
    }, []);

    const tableRowColor = useCallback((survey?: ExtendedSurvey) => {
        if (survey?.statusId === SurveyStatusIdEnum.Draft) {
            return { color: '#00a3ff' };
        }

        if (survey?.statusId === SurveyStatusIdEnum.Live) {
            return { color: '#00AE7A' };
        }

        return {};
    }, []);

    const renderActionButton = useCallback(
        (survey?: ExtendedSurvey) => {
            if (survey?.statusId === SurveyStatusIdEnum.Draft) {
                return (
                    <Button
                        className="Surveys__button-launch"
                        modifiers={['rounded']}
                        onClick={() => actions.onLaunch(survey)}
                    >
                        Launch
                    </Button>
                );
            }

            if (survey?.statusId === SurveyStatusIdEnum.Live) {
                return (
                    <Button
                        className="Surveys__button-close"
                        modifiers={['rounded']}
                        onClick={() => actions.onClose(survey)}
                    >
                        Close
                    </Button>
                );
            }

            if (survey?.statusId === SurveyStatusIdEnum.Closed && survey.id) {
                return <ClosedSurveyActionButton surveyId={survey.id} />;
            }

            return null;
        },
        [actions],
    );

    const skeleton = useMemo(() => <Skeleton sx={{ margin: 'auto' }} width={60} height={40} animation="wave" />, []);

    const tableConfig = useMemo(
        (): iTableConfigItem[] => [
            {
                key: 'surveyId',
                title: 'Survey ID',
                Value: ({ index }) => (
                    <div style={{ ...tableRowColor(sortedResults?.[index]) }}>{sortedResults?.[index]?.id}</div>
                ),
                width: '70px',
            },
            {
                key: 'fieldingName',
                sortingColName: 'name',
                title: 'Fielding Name',
                Value: ({ index }) => (
                    <div style={{ ...tableRowColor(sortedResults?.[index]) }}>{sortedResults?.[index]?.name}</div>
                ),
                width: '140px',
            },
            {
                key: 'dateCreated',
                sortingColName: 'createdDate',
                title: 'Date Created',
                Value: ({ index }) => (
                    <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                        {formatDefaultDate(sortedResults?.[index]?.createdDate)}
                    </div>
                ),
                width: '70px',
            },
            {
                key: 'fieldingStartDate',
                sortingColName: 'startDate',
                title: 'Fielding Start Date',
                Value: ({ index }) => (
                    <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                        {formatDefaultDate(sortedResults?.[index]?.startDate)}
                    </div>
                ),
                width: '70px',
            },
            {
                key: 'fieldingEndDate',
                sortingColName: 'endDate',
                title: 'Fielding End Date',
                Value: ({ index }) => (
                    <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                        {formatDefaultDate(sortedResults?.[index]?.endDate)}
                    </div>
                ),
                width: '70px',
            },
            {
                key: 'status',
                title: 'Status',
                sortingColName: 'statusId',
                Value: ({ index }) => {
                    if (isLoadingCommon) return skeleton

                    if (sortedResults?.[index]?.statusId !== undefined) {
                        return (
                            <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                                {findCommonNameById('surveyStatuses', sortedResults[index].statusId)}
                            </div>
                        );
                    }

                    return null;
                },
                width: '65px',
            },
            {
                key: 'express',
                sortingColName: 'expressCount',
                title: 'Express',
                Value: ({ index }) => (
                    isExtendedDataLoading ? (
                        skeleton
                    ) : (
                        <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                            {formatNumber(sortedResults?.[index]?.expressCount)}
                        </div>
                    )
                ),
                width: '70px',
            },
            {
                key: 'fullTest',
                sortingColName: 'fullTestCount',
                title: 'Full Test',
                Value: ({ index }) => (
                    isExtendedDataLoading ? (
                        skeleton
                    ) : (
                        <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                            {formatNumber(sortedResults?.[index]?.fullTestCount)}
                        </div>
                    )
                ),
                width: '70px',
            },
            {
                key: 'publicItems',
                sortingColName: 'publicItemsCount',
                title: 'Public Items',
                Value: ({ index }) => (
                    isExtendedDataLoading ? (
                        skeleton
                    ) : (
                        <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                            {formatNumber(sortedResults?.[index]?.publicItemsCount)}
                        </div>
                    )
                ),
                width: '70px',
            },
            {
                key: 'totalItems',
                sortingColName: 'totalItemsCount',
                title: 'Total Items',
                Value: ({ index }) => (
                    isExtendedDataLoading ? (
                        skeleton
                    ) : (
                        <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                            {formatNumber(sortedResults?.[index]?.totalItemsCount)}
                        </div>
                    )
                ),
                width: '70px',
            },
            {
                key: 'sampleSize',
                sortingColName: 'completedSampleSize',
                title: 'Sample',
                Value: ({ index }) => (
                    isExtendedDataLoading ? (
                        skeleton
                    ) : (
                        <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                            {getSampleSize(sortedResults?.[index])}
                        </div>
                    )
                ),
                width: '70px',
            },
            {
                key: 'customSampleType',
                title: 'Custom Sample Type',
                Value: ({ index }) => (
                    isExtendedDataLoading ? (
                        skeleton
                    ) : (
                        <div style={{ ...tableRowColor(sortedResults?.[index]) }}>
                            {getCustomFielding(sortedResults?.[index]?.customSampleTypeId)}
                        </div>
                    )
                ),
                width: '80px',
            },
            {
                key: 'action',
                width: '120px',
                title: 'Actions',
                Value: ({ index }) => (
                    <div className="Surveys__table-actions">
                        <IconButton onClick={() => actions.onEdit(sortedResults?.[index])}>
                            <IconPencil />
                        </IconButton>
                        <div className='Surveys__button-download'>
                            {renderActionButton(sortedResults?.[index])}
                        </div>
                    </div>
                ),
            },
        ],
        [
            actions,
            findCommonNameById,
            getCustomFielding,
            renderActionButton,
            sortedResults,
            tableRowColor,
            getSampleSize,
            isExtendedDataLoading,
            isLoadingCommon,
            skeleton,
        ],
    );

    const contextValue = useMemo(() => ({ config: tableConfig, sortConfig }), [tableConfig, sortConfig])

    return <TableContext.Provider value={contextValue}>{children}</TableContext.Provider>;
};

export default SurveysTableConfig;
